import { apiRequest, isNotFoundError } from "src/utils/apiRequest";

export interface Evidence {
    evidenceId: string;
    uri: string;
    fileType: string;
    presignedUrl?: string; // Short-lived pre-signed s3 URL returned by the backend for direct consumption
}

export interface ObjectPresenceCheck {
    object_name: string;
    is_compliant: boolean;
    ai_reason: string;
    /** Inspector's per-object override justification. */
    overrideReason?: string | null;
    overriddenEvidences?: Evidence[];
    originalIsCompliant?: boolean;
}

export interface InspectionResult {
    resultId: string;
    questionId: string;
    objectId: string | null;
    aiResult: string;
    aiReasoning: string;
    confirmedResult: string;
    isCompliant: boolean;
    isInspectorOverride: boolean;
    overrideReason: string | null;
    /** Optional free-text note the inspector entered when answering a manual question. */
    userComment?: string | null;
    /** System-computed value for auto-calculated questions (e.g. hazardous container
     *  count). Preserved across an inspector override so the review sheet can show the
     *  original calculated value alongside the overridden one. */
    calculatedValue?: string | null;
    evidence: Evidence[];
    overriddenEvidences?: Evidence[];
    originalIsCompliant?: boolean;
    objectPresenceChecks?: ObjectPresenceCheck[];
}

export interface InspectionData {
    inspectionId: string;
    austinInspectionId?: string; // Present only when linked to an Austin scheduled inspection
    templateId: string;
    templateName: string;
    siteCode: string;
    createdBy: string;
    createdAt: string;
    completedAt: string | null;
    status: string;
    expiresAt?: number; // Unix epoch SECONDS when a draft is auto-cleared server-side (present on draft payloads)
    results: InspectionResult[];
}

export interface InspectionWithPresignedUrls {
    inspectionData: InspectionData;
    presignedUrlMap: Record<string, string>;
    groupedResults: Record<string, InspectionResult[]>;
}

export type InspectionStatus = "In_Progress" | "Completed";

/**
 * A completed inspection as shown in the History views. Mirrors the backend
 * InspectionListView: identity + submission timestamp (createdAt) + compliance
 * counts, without the full results payload.
 */
export interface InspectionListView {
    inspectionId: string;
    templateId: string;
    templateName: string;
    siteCode: string;
    createdAt: string;
    status: InspectionStatus;
    complianceStats: {
        compliantCount: number;
        questionCount: number;
    };
}

export type UpdateInspectionPayload = {
  inspectionId: string;
  templateId: string;
  templateName: string;
  siteCode: string;
  status: InspectionStatus;
  createdBy: string;
  createdAt: string;
};

/**
 * Shared helper to determine whether an item has been manually overridden.
 * Works with both entity types in the data model:
 *  - InspectionResult (drum questions): uses `isInspectorOverride`
 *  - ObjectPresenceCheck (object-level): uses `overriddenEvidences.length > 0`
 */
export function isOverridden(item: InspectionResult | ObjectPresenceCheck): boolean {
    if ("isInspectorOverride" in item) {
        return item.isInspectorOverride;
    }
    return (item.overriddenEvidences?.length ?? 0) > 0;
}

/**
 * Resolves which comment a detail sheet should display, based on which tab/state it is
 * currently presenting. Shared by ComplianceDetailSheet (InspectionResult) and
 * ObjectPresenceDetailSheet (ObjectPresenceCheck), which otherwise duplicate this exact
 * branch against their own field names.
 *
 * - Viewing the "Overridden" state (or a result with no tabs at all, still the overridden
 *   view): show the override justification. A reason is mandatory on every override, so
 *   this is always present.
 * - Viewing the "Original result" tab, or the item was never overridden: show the original
 *   answer-time comment.
 */
export function resolveDisplayedComment(
    isItemOverridden: boolean,
    isOriginalTabActive: boolean,
    overrideReason: string | null | undefined,
    userComment: string | null | undefined
): string | null | undefined {
    const isViewingOverride = isItemOverridden && !isOriginalTabActive;
    if (!isViewingOverride) {
        return userComment;
    }
    return overrideReason?.trim();
}

class InspectionService {
    /**
     * Triggers the backend auto-calculation for a given question within an
     * inspection. The backend computes the result automatically (no user input
     * required) and returns the resulting InspectionResult.
     */
    async autoCalculate(inspectionId: string, questionId: string): Promise<InspectionResult> {
        const data = await apiRequest<InspectionResult>({
            url: `/inspections/auto-calculate`,
            method: "POST",
            data: {
                inspectionId: inspectionId,
                questionId: questionId,
            },
        });
        return data;
    }

    async completeInspection(payload: UpdateInspectionPayload) {
        const data = await apiRequest({
            url: `/inspections`,
            method: "POST",
            data: payload,
        });
        return data;
    }

    /**
     * Complete an inspection and submit it. The backend marks the inspection
     * COMPLETED and, if it is linked to an Austin scheduled inspection, starts
     * and submits it to Austin/RCMS. Returns the completed inspection.
     */
    async submitInspection(inspectionId: string): Promise<InspectionData> {
        const data = await apiRequest({
            url: `/inspections/submit`,
            method: "POST",
            data: { inspectionId },
        });
        return data;
    }

    /**
     * Copy a container's label-question results from a prior inspection into this
     * one, skipping a fresh capture and AI analysis entirely. Called instead of the
     * normal capture-and-analyze flow when a container qualifies to skip the label
     * capture step. Returns the finished results synchronously -- no PENDING/poll,
     * since no AI analysis runs.
     */
    async reuseLabelResult(
        inspectionId: string,
        objectId: string,
        sourceInspectionId: string
    ): Promise<InspectionResult[]> {
        const data = await apiRequest<InspectionResult[]>({
            url: `/inspections/reuse-label-result`,
            method: "POST",
            data: { inspectionId, objectId, sourceInspectionId },
        });
        return data;
    }

    async recordManualResult(payload: FormData) {
        const data = await apiRequest({
            url: `/inspections/record-manual-result`,
            method: "POST",
            data: payload,
        });
        return data;
    }

    /**
     * List all COMPLETED inspections for a site, newest first, for the History views.
     * Not user-scoped: returns completed inspections regardless of which inspector
     * created them. Scoped by templateName rather than templateId: Austin inspections
     * all share the same templateId ("WorkflowDriven-generated"), so templateName (the
     * Austin inspection's display name) is what actually distinguishes inspection types.
     * Optionally scope to a createdAt date window (startDate/endDate must be supplied
     * together, in the same ISO format the backend stores createdAt in).
     */
    async getInspectionHistory(params: {
        siteCode: string;
        templateName?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<InspectionListView[]> {
        const data = await apiRequest<InspectionListView[]>({
            url: `/inspections/history`,
            method: "GET",
            params,
        });
        return data;
    }

    /**
     * Looks up a resumable draft for a scheduled (Austin) inspection.
     * A 404 from the backend is an expected "no resumable draft" signal, so it
     * is surfaced as `null` rather than thrown. Any other error (400/5xx/network)
     * is rethrown for the caller to handle.
     */
    async getDraftInspection(austinInspectionId: string): Promise<InspectionData | null> {
        try {
            const data = await apiRequest<InspectionData>({
                url: `/inspections/draft`,
                method: "GET",
                params: { austinInspectionId },
            });
            return data;
        } catch (err) {
            if (isNotFoundError(err)) return null; // 404 -> no draft
            throw err; // 400/5xx/network -> real error
        }
    }

    async fetchInspectionWithPresignedUrls(
        inspectionId: string
    ): Promise<InspectionWithPresignedUrls> {
        const data = await apiRequest({
            url: `/inspections/${inspectionId}`,
            method: "GET",
        });

        // The backend returns a presigned s3 URL on each evidence item, so no
        // separate presigning call is needed. Build a S3-URI -> presigned-URL map
        // from the payload to keep the shape the review components already consume.
        const allEvidence = (data.results ?? []).flatMap((result: InspectionResult) => [
            ...(result.evidence ?? []),
            ...(result.overriddenEvidences ?? []),
            ...(result.objectPresenceChecks ?? []).flatMap((check: ObjectPresenceCheck) =>
                check.overriddenEvidences ?? []
            ),
        ]);

        const presignedUrlMap: Record<string, string> = {};
        for (const ev of allEvidence) {
            if (ev.uri?.startsWith('s3://') && ev.presignedUrl) {
                presignedUrlMap[ev.uri] = ev.presignedUrl;
            }
        }

        // Group results by questionId
        const groupedResults: Record<string, InspectionResult[]> = {};
        data.results?.forEach((result: InspectionResult) => {
            if (!groupedResults[result.questionId]) {
                groupedResults[result.questionId] = [];
            }
            groupedResults[result.questionId].push(result);
        });

        return {
            inspectionData: data,
            presignedUrlMap,
            groupedResults,
        };
    }
}

export default new InspectionService();
